import { useMemo, useState } from "react";
import Cookies from "js-cookie";
import { TextField } from "../../common/inputFiled";
import OtpModal from "../../common/OtpModal";
import { LocationModal } from "../../profileComponents/locationModel";
import { appStorage } from "../../../utils/appStorage";
import { updateAuthTokenFromResponse } from "../../../utils/authSession";
import toast from "react-hot-toast";

const MARITAL_STATUS_OPTIONS = [
  { value: "Single", label: "Single" },
  { value: "Married", label: "Married" },
  { value: "Divorced", label: "Divorced" },
  { value: "Widowed", label: "Widowed" },
  { value: "Separated", label: "Separated" },
];

const isEmail = (val) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(String(val || "").trim());

const getTodayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const buildPincodeUpdates = (place = {}, country = "", post_code = "") => {
  const placeName = place?.place_name || place?.name || place?.city || "";
  const state = place?.state || place?.region || "";
  const city = place?.city || place?.locality || placeName || "";
  const countryValue = country || place?.country || "";
  const pin = String(post_code || place?.pin_code || "").trim();
  const cityRegionCountry = [city, state, countryValue].filter(Boolean).join(", ");
  const currentCityRegionCountry = [placeName || city, state, countryValue].filter(Boolean).join(", ");

  return {
    basic_pincode_home: pin,
    home_address_pincode: pin,
    current_city_region_country: currentCityRegionCountry || cityRegionCountry,
    home_address_city: city,
    home_address_region: state,
    home_address_country: countryValue,
  };
};

export default function AppSetupPersonalInfoStep({ signupDraft, onSubmit, loading }) {
  const [sameAsAboveEmail, setSameAsAboveEmail] = useState(true);
  const [sameAsPersonalWorkMobile, setSameAsPersonalWorkMobile] = useState(true);
  const [errors, setErrors] = useState({});
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [workEmailVerified, setWorkEmailVerified] = useState(true);
  const [inlineEditingWorkEmail, setInlineEditingWorkEmail] = useState(false);
  const [workEmailDraft, setWorkEmailDraft] = useState("");
  const [fieldSaving, setFieldSaving] = useState("");
  const [otpModalState, setOtpModalState] = useState({
    open: false,
    contactLabel: "",
    contactValue: "",
  });

  const [formData, setFormData] = useState(() => {
    const signupEmail = signupDraft?.email || appStorage.getItem("email") || "";
    const signupPhone = signupDraft?.phoneNumber || appStorage.getItem("phone_number") || "";
    const signupCode = signupDraft?.countryCode || appStorage.getItem("country_code") || "+91";
    const firstName = signupDraft?.firstName || "";
    const lastName = signupDraft?.lastName || "";

    return {
      first_name: firstName,
      last_name: lastName,
      full_name: [firstName, lastName].filter(Boolean).join(" "),
      personal_email_id: signupEmail,
      work_email_id: signupEmail,
      personal_mobile: signupPhone,
      work_mobile: signupPhone,
      country_code_personal: signupCode,
      work_mobile_country_code: signupCode,
      country_code_home: "+91",
      country_code_home_fixed: "+91",
      country_code_work_fixed: "+91",
      secondary_email: "",
      secondary_mobile: "",
      home_fixed: "",
      work_fixed: "",
      marital_status: "",
      date_of_birth: "",
      date_of_anniversary: "",
      birth_city_country: "",
      current_city_region_country: "",
      basic_pincode_home: "",
      home_address_pincode: "",
      home_address_city: "",
      home_address_region: "",
      home_address_country: "",
      personal_mobile_valid: true,
      work_mobile_valid: true,
    };
  });

  const summaryItems = useMemo(
    () => [
      { label: "Name", value: formData.full_name },
      { label: "Email", value: formData.personal_email_id },
      {
        label: "Mobile",
        value: `${formData.country_code_personal || ""} ${formData.personal_mobile || ""}`.trim(),
      },
    ],
    [formData]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (sameAsAboveEmail && name === "personal_email_id") {
        next.work_email_id = value;
      }
      return next;
    });
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handlePlaceSelect = ({ place, country, post_code }) => {
    const updates = buildPincodeUpdates(place || {}, country, post_code);
    setFormData((prev) => ({ ...prev, ...updates }));
    setErrors((prev) => ({
      ...prev,
      basic_pincode_home: "",
      current_city_region_country: "",
    }));
    setShowLocationModal(false);
    toast.success("Location updated");
  };

  const closeOtpModal = () => {
    setOtpModalState({ open: false, contactLabel: "", contactValue: "" });
  };

  const handleToggleSameAsAboveEmail = () => {
    setSameAsAboveEmail((prev) => {
      const next = !prev;
      setFormData((fd) => {
        const workEmail = next ? fd.personal_email_id : fd.work_email_id;
        const isSameAsPersonal =
          String(workEmail || "").trim().toLowerCase() ===
          String(fd.personal_email_id || "").trim().toLowerCase();
        setWorkEmailVerified(next || isSameAsPersonal);
        return { ...fd, work_email_id: workEmail };
      });
      if (next) {
        setInlineEditingWorkEmail(false);
        setWorkEmailDraft("");
      }
      setErrors((err) => ({ ...err, work_email_id: "" }));
      return next;
    });
  };

  const getWorkEmailActionButton = () => {
    if (sameAsAboveEmail) return null;

    const isActive = inlineEditingWorkEmail;
    const isSaving = fieldSaving === "work_email_id";

    return (
      <button
        type="button"
        onClick={async () => {
          if (isSaving) return;
          if (isActive) {
            const value = String(workEmailDraft || "").trim();
            if (!value) {
              toast.error("Enter Work Email");
              return;
            }
            if (!isEmail(value)) {
              toast.error("Enter a valid email");
              return;
            }
            if (value === String(formData.personal_email_id || "").trim()) {
              setFormData((prev) => ({ ...prev, work_email_id: value }));
              setWorkEmailVerified(true);
              setInlineEditingWorkEmail(false);
              toast.success("Work email same as personal email");
              return;
            }

            setFieldSaving("work_email_id");
            try {
              const token = Cookies.get("auth_token");
              const checkPayload = new URLSearchParams({ work_email_id: value });
              const checkRes = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/work-email/check`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    Authorization: `Bearer ${token}`,
                  },
                  body: checkPayload.toString(),
                }
              );
              const checkData = await checkRes.json();
              if (String(checkData?.status) !== "1") {
                throw new Error(checkData?.message || "Work email already exists");
              }

              const sendRes = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/work-email/send-otp`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    Authorization: `Bearer ${token}`,
                  },
                  body: checkPayload.toString(),
                }
              );
              const sendData = await sendRes.json();
              if (String(sendData?.status) !== "1") {
                throw new Error(sendData?.message || "Failed to send OTP");
              }

              toast.success(sendData?.message || "OTP sent successfully");
              setOtpModalState({
                open: true,
                contactLabel: `Work Email Id: ${value}`,
                contactValue: value,
              });
            } catch (error) {
              toast.error(error.message || "Unable to verify work email");
            } finally {
              setFieldSaving("");
            }
            return;
          }

          setWorkEmailDraft(formData.work_email_id || "");
          setInlineEditingWorkEmail(true);
          setWorkEmailVerified(false);
        }}
        className="inline-flex items-center gap-1 rounded-full bg-[#F3F0FF] px-3 py-1 text-[11px] font-semibold text-[#6938EF] shadow-sm transition hover:bg-[#E7DFFF]"
      >
        {isSaving ? (
          <>
            <span className="h-3 w-3 animate-spin rounded-full border-2 border-[#6938EF] border-t-transparent" />
            Saving
          </>
        ) : isActive ? (
          "Save"
        ) : (
          "Edit"
        )}
      </button>
    );
  };

  const handleVerifyWorkEmailOtp = async (otpValue) => {
    const contactValue = otpModalState.contactValue;
    setFieldSaving("work_email_id");
    try {
      const token = Cookies.get("auth_token");
      const payload = new URLSearchParams({
        work_email_id: contactValue,
        otp: otpValue,
      });
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/work-email/verify-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
        body: payload.toString(),
      });
      const data = await res.json();
      if (String(data?.status) !== "1") {
        throw new Error(data?.message || "Invalid OTP");
      }

      updateAuthTokenFromResponse(data);
      setFormData((prev) => ({ ...prev, work_email_id: contactValue }));
      setWorkEmailVerified(true);
      setInlineEditingWorkEmail(false);
      closeOtpModal();
      toast.success(data?.message || "Work email verified");
    } catch (error) {
      toast.error(error.message || "OTP verification failed");
    } finally {
      setFieldSaving("");
    }
  };

  const validate = () => {
    const nextErrors = {};
    const workEmail = sameAsAboveEmail ? formData.personal_email_id : formData.work_email_id;

    if (!String(workEmail || "").trim()) nextErrors.work_email_id = "Enter Work Email";
    if (!sameAsPersonalWorkMobile && !String(formData.work_mobile || "").trim()) {
      nextErrors.work_mobile = "Enter Work Mobile";
    }
    if (!String(formData.basic_pincode_home || "").trim()) {
      nextErrors.basic_pincode_home = "Enter Pincode - Home";
    }
    if (!String(formData.current_city_region_country || "").trim()) {
      nextErrors.current_city_region_country = "Enter Current City, Region and Country";
    }
    if (!String(formData.marital_status || "").trim()) nextErrors.marital_status = "Select Marital Status";
    if (!String(formData.date_of_birth || "").trim()) nextErrors.date_of_birth = "Enter Date of Birth";
    if (formData.marital_status === "Married" && !String(formData.date_of_anniversary || "").trim()) {
      nextErrors.date_of_anniversary = "Enter Anniversary Date";
    }
    if (workEmail && !isEmail(workEmail)) {
      nextErrors.work_email_id = "Enter a valid email";
    }
    if (!sameAsAboveEmail && !workEmailVerified) {
      nextErrors.work_email_id = "Verify work email with OTP";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields");
      return;
    }

    const payload = {
      ...formData,
      work_email_id: sameAsAboveEmail ? formData.personal_email_id : formData.work_email_id,
      work_mobile: sameAsPersonalWorkMobile ? formData.personal_mobile : formData.work_mobile,
      work_mobile_country_code: sameAsPersonalWorkMobile
        ? formData.country_code_personal
        : formData.work_mobile_country_code,
      work_mobile_valid: true,
    };

    onSubmit(payload);
  };

  return (
    <>
      {showLocationModal && (
        <LocationModal
          show={showLocationModal}
          onClose={() => setShowLocationModal(false)}
          onSelect={handlePlaceSelect}
          initialPincode={formData.basic_pincode_home || ""}
        />
      )}

      <OtpModal
        open={otpModalState.open}
        title="Verify Work Email"
        description="Enter the 6-digit code sent to your work email."
        contactLabel={otpModalState.contactLabel}
        loading={fieldSaving === "work_email_id"}
        onClose={closeOtpModal}
        onVerify={handleVerifyWorkEmailOtp}
      />

      <div className="mobile-scroll-y h-[var(--app-height,100dvh)] bg-[#F3F0FF] px-4 py-6 sm:py-8">
        <div className="mx-auto w-full max-w-[520px] p-4 sm:p-8">
          <div className="mb-6 text-center">
            <h2 className="text-2xl font-semibold text-gray-900">Personal Information</h2>
            <p className="mt-2 text-sm text-gray-600">
              Complete your basic profile details. Fields marked with * are required.
            </p>
          </div>

          <div className="mb-5 rounded-2xl border border-violet-100 bg-white p-4 shadow-sm">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-[#643ECF]">From signup</p>
            <div className="space-y-2">
              {summaryItems.map((item) => (
                <div key={item.label} className="flex justify-between gap-3 text-sm">
                  <span className="text-gray-500">{item.label}</span>
                  <span className="font-medium text-gray-900 text-right break-all">{item.value || "—"}</span>
                </div>
              ))}
            </div>
          </div>

          <form className="space-y-4 rounded-3xl border border-violet-100 bg-white p-6 shadow-xl shadow-violet-200/30 pb-24" onSubmit={handleSubmit} noValidate>
            <div>
              <div className="mb-1 flex items-center justify-between gap-2">
                <label className="text-xs input-lable">Work Email Id<span className="text-red-500">*</span></label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-sm text-gray-500">Same as above</span>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={sameAsAboveEmail}
                    onChange={handleToggleSameAsAboveEmail}
                  />
                  <div className="relative inline-flex h-5 w-9 items-center rounded-full bg-gray-200 peer-checked:bg-[#6938EF] after:absolute after:left-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:after:translate-x-[16px]" />
                </label>
              </div>
              <TextField
                name="work_email_id"
                value={inlineEditingWorkEmail ? workEmailDraft : formData.work_email_id}
                onChange={(e) => {
                  if (inlineEditingWorkEmail) {
                    setWorkEmailDraft(e.target.value);
                    setWorkEmailVerified(false);
                  } else {
                    handleChange(e);
                  }
                  if (errors.work_email_id) setErrors((p) => ({ ...p, work_email_id: "" }));
                }}
                disabled={sameAsAboveEmail || (!inlineEditingWorkEmail && !sameAsAboveEmail)}
                startIcon="./input-logos/sms.svg"
                placeholder="Enter Work Email"
                rightIcon={getWorkEmailActionButton()}
                error={errors.work_email_id}
              />
              {!sameAsAboveEmail && workEmailVerified && (
                <p className="mt-1 text-xs text-green-600">Work email verified</p>
              )}
            </div>

            <div>
              <div className="mb-1 flex items-center justify-between gap-2">
                <label className="text-xs input-lable">Work Mobile<span className="text-red-500">*</span></label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-sm text-gray-500">Same as Mobile Number</span>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={sameAsPersonalWorkMobile}
                    onChange={() => setSameAsPersonalWorkMobile((prev) => !prev)}
                  />
                  <div className="relative inline-flex h-5 w-9 items-center rounded-full bg-gray-200 peer-checked:bg-[#6938EF] after:absolute after:left-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:after:translate-x-[16px]" />
                </label>
              </div>
              {!sameAsPersonalWorkMobile && (
                <TextField
                  name="work_mobile"
                  value={formData.work_mobile}
                  onChange={handleChange}
                  startIcon="./input-logos/call.svg"
                  placeholder="Enter Work Mobile"
                  error={errors.work_mobile}
                />
              )}
            </div>

            <TextField
              label="Pincode - Home"
              name="basic_pincode_home"
              required
              value={formData.basic_pincode_home}
              placeholder="Click to select pincode"
              onClick={() => setShowLocationModal(true)}
              onFocus={(e) => {
                e.target.blur();
                setShowLocationModal(true);
              }}
              readOnly
              rightIcon="../input-logos/location.svg"
              error={errors.basic_pincode_home}
            />

            <TextField
              label="Current City, Region and Country"
              name="current_city_region_country"
              required
              value={formData.current_city_region_country}
              placeholder="Autofilled from pincode"
              disabled
              error={errors.current_city_region_country}
            />

            <TextField
              label="Marital Status"
              name="marital_status"
              required
              select
              value={formData.marital_status}
              onChange={(e) => {
                handleChange(e);
                if (e.target.value !== "Married") {
                  setFormData((prev) => ({ ...prev, date_of_anniversary: "" }));
                  setErrors((prev) => ({ ...prev, date_of_anniversary: "" }));
                }
                if (errors.marital_status) setErrors((prev) => ({ ...prev, marital_status: "" }));
              }}
              options={MARITAL_STATUS_OPTIONS}
              startIcon="./input-logos/ring.svg"
              error={errors.marital_status}
            />

            <TextField
              label="Date of Birth"
              name="date_of_birth"
              required
              type="date"
              value={formData.date_of_birth}
              onChange={handleChange}
              max={getTodayISO()}
              startIcon="./input-logos/calander.svg"
              error={errors.date_of_birth}
            />

            {formData.marital_status === "Married" && (
              <TextField
                label="Anniversary Date"
                name="date_of_anniversary"
                required
                type="date"
                value={formData.date_of_anniversary}
                onChange={handleChange}
                max={getTodayISO()}
                startIcon="./input-logos/calander.svg"
                error={errors.date_of_anniversary}
              />
            )}

            <button
              type="submit"
              disabled={loading}
              className="submit-button w-full rounded-xl py-3.5 text-sm font-semibold shadow-lg shadow-violet-500/25 disabled:opacity-60"
            >
              {loading ? "Saving..." : "Continue"}
            </button>
          </form>
        </div>
      </div>
    </>
  );
}
